import { isEssayInstructionBlob } from './essayChoiceGroup';
import { isTaraWritingFormatG, resolveEssayPrompts } from './taraWritingPrompts';

function takingItemsFromSource(source, prompts, paperId) {
  const sourceKey = String(source.db_id ?? source.id ?? paperId ?? 'tara-writing');
  return prompts.map((opt, index) => ({
    ...source,
    id: `tara-w-${sourceKey}-p${index}`,
    writingSourceId: source.id,
    writingPromptIndex: index,
    writingPromptLabel: opt.label,
    question: opt.text,
    tmuaPaper: 1,
    tmuaSourcePaperId: paperId,
    pearsonWritingItem: true,
  }));
}

function standalonePrompt(source) {
  const qText = String(source?.question || '').trim();
  const isEssayRow = Boolean(source?.is_essay || source?.interaction_kind === 'essay_choice_group');
  if (!isEssayRow || !qText || isEssayInstructionBlob(qText)) return null;
  return { label: 'A', text: qText };
}

/**
 * Expands TARA Writing essay rows into Pearson taking-items.
 * Default requireFormatG=true keeps mocks at exactly 3 prompts.
 * Practice callers pass { requireFormatG: false } for N≥1 real prompts.
 */
export function expandTaraWritingPearsonItems(questions, paperId, { requireFormatG = true } = {}) {
  if (requireFormatG) {
    if (!isTaraWritingFormatG(questions)) return [];
    const source = questions[0];
    return takingItemsFromSource(source, resolveEssayPrompts(source), paperId);
  }

  const raw = Array.isArray(questions) ? questions : [];
  const items = [];
  raw.forEach((source) => {
    const prompts = resolveEssayPrompts(source);
    if (prompts.length) {
      items.push(...takingItemsFromSource(source, prompts, paperId));
      return;
    }
    const standalone = standalonePrompt(source);
    if (standalone) {
      items.push(...takingItemsFromSource(source, [standalone], paperId));
    }
  });
  return items;
}

/**
 * Pause/complete look up userAnswers by question id. Format-G writing is
 * answered on the three expanded prompt ids, not the single source row, so
 * persistence has to use those ids or the essay never leaves the browser.
 * LNAT Section B is answered on its own ids (expandLnatSectionBQuestions),
 * so re-expanding it here would lose the essay the same way.
 */
function isLnatRow(q) {
  return [q.tmuaSourcePaperId, q.parameter, q.paper_id, q.external_question_id]
    .some((v) => /(^|[^a-z])lnat([^a-z]|$)/i.test(String(v || '')));
}

export function questionsForAttemptPayloads(questions) {
  const list = Array.isArray(questions) ? questions : [];
  const out = [];
  list.forEach((q) => {
    if (!q) return;
    if (q.pearsonWritingItem || isLnatRow(q)) {
      out.push(q);
      return;
    }
    const expanded = expandTaraWritingPearsonItems(
      [q],
      q.tmuaSourcePaperId,
      { requireFormatG: false }
    );
    if (expanded.length) {
      out.push(...expanded);
      return;
    }
    out.push(q);
  });
  return out;
}

export function hasAnsweredOneTaraWritingPrompt(userAnswers = {}, questions = []) {
  const items = questionsForAttemptPayloads(questions);
  return items.some((q) => {
    const text = userAnswers[q.id]?.text_response;
    return Boolean(text && String(text).trim());
  });
}
